import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Plus, X } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import ConfirmModal from '../../ui/ConfirmModal';
import ErrorCard from '../../ui/ErrorCard';
import Input from '../../ui/Input';
import Modal from '../../ui/Modal';
import { MONO_CLASS, Sku } from '../../ui/Mono';
import { SkeletonTable } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import { useToast } from '../../ui/Toast';
import { ProductPicker } from '../lpnPickers';
import { ExtractListRows } from '../payload';
import { useProductionLabels } from './productionLabels';

/**
 * CADASTROS QUE LIGAM DOIS PRODUTOS — ficha técnica (mãe → filha) e opções de
 * kit (kit → componente).
 *
 * ── Por que não é o `RegistryManager` ───────────────────────────────────────
 * O RegistryManager do módulo resolve cadastro de `code`/`label`/flags, e é o
 * que as abas de Configurações usam. Estes dois não cabem nele por um motivo
 * concreto: os campos-chave são PRODUTOS do catálogo do Bling (centenas de
 * linhas), que se escolhem por BUSCA — o `ProductPicker` do M3 —, não por
 * `<select>`. Forçar um select de catálogo inteiro seria inutilizável no
 * teclado, e alargar o RegistryManager para conhecer produto o transformaria no
 * componente-que-sabe-de-tudo.
 *
 * O resto do comportamento é o MESMO do módulo, de propósito: listar, criar em
 * modal, editar só o que o backend deixa (os percentuais / a quantidade), e
 * ativar/desativar — **sem DELETE**, política do projeto.
 *
 * `config`:
 *  - title / description / countLabel / exportFileName / entityLabel
 *  - list() / create(payload) / update(id, patch) / listKeys
 *  - productFields: [{ name, fieldLabel, skuKey, nameKey }] — os dois produtos
 *  - numberFields: [{ name, fieldLabel, min, max, step, help, transient }]
 *    (`transient` = vai no POST mas não é coluna do registro — é o caso do
 *    `kit_size`, que o backend usa só para CONFERIR a combinação e não grava)
 */
const ProductionRecipeManager = ({ config }) => {
    const { label } = useProductionLabels();
    const toast = useToast();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [products, setProducts] = useState({});
    const [numbers, setNumbers] = useState({});
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [rowToToggle, setRowToToggle] = useState(null);
    const [toggleLoading, setToggleLoading] = useState(false);

    const { list, listKeys } = config;

    const loadRows = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const data = await list({ limit: 500 });
            setRows(ExtractListRows(data, listKeys));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [list, listKeys]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const openCreate = () => {
        setEditingRow(null);
        setProducts({});
        setNumbers(
            Object.fromEntries(config.numberFields.map((field) => [field.name, field.defaultValue ?? ''])),
        );
        setFormError(null);
        setModalOpen(true);
    };

    const openEdit = useCallback(
        (row) => {
            setEditingRow(row);
            setProducts({});
            setNumbers(
                Object.fromEntries(
                    config.numberFields.map((field) => [
                        field.name,
                        // Campo transitório (kit_size) não existe na linha: o
                        // registro não o guarda — quem quiser reconferir digita.
                        field.transient ? '' : String(row?.[field.name] ?? ''),
                    ]),
                ),
            );
            setFormError(null);
            setModalOpen(true);
        },
        [config.numberFields],
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        const payload = {};

        if (!editingRow) {
            for (const field of config.productFields) {
                const chosen = products[field.name];
                if (!chosen?.product_id) {
                    setFormError({ message: label('wms.production.recipe_pick_products') });
                    return;
                }
                payload[field.name] = chosen.product_id;
            }
        }

        for (const field of config.numberFields) {
            const raw = String(numbers[field.name] ?? '').trim();
            if (raw === '') {
                if (field.required && !editingRow) {
                    setFormError({ field: field.name, message: label('wms.common.required') });
                    return;
                }
                continue;
            }
            payload[field.name] = Number(raw);
        }

        setSaving(true);
        setFormError(null);
        try {
            if (editingRow) {
                await config.update(editingRow.id, payload);
            } else {
                await config.create(payload);
            }
            toast.success(config.entityLabel);
            setModalOpen(false);
            await loadRows();
        } catch (error) {
            setFormError({ message: error?.message || 'Erro ao salvar' });
        } finally {
            setSaving(false);
        }
    };

    const confirmToggle = async () => {
        if (!rowToToggle) {
            return;
        }
        const nextActive = !rowToToggle.active;
        setToggleLoading(true);
        try {
            await config.update(rowToToggle.id, { active: nextActive });
            await loadRows();
        } catch (error) {
            toast.error(error?.message || 'Erro ao salvar');
        } finally {
            setToggleLoading(false);
            setRowToToggle(null);
        }
    };

    const columns = useMemo(
        () => [
            ...config.productFields.map((field) => ({
                key: field.name,
                label: field.fieldLabel,
                getValue: (row) => `${row[field.skuKey] || ''} ${row[field.nameKey] || ''}`.trim(),
                render: (row) => (
                    <span className="block min-w-0">
                        <Sku value={row[field.skuKey]} className="text-xs font-medium" />
                        <span className="block truncate text-xs text-mono-500 dark:text-mono-400">
                            {row[field.nameKey] || ''}
                        </span>
                    </span>
                ),
            })),
            ...config.numberFields
                .filter((field) => !field.transient)
                .map((field) => ({
                    key: field.name,
                    label: field.fieldLabel,
                    align: 'right',
                    getValue: (row) => Number(row[field.name] ?? 0),
                    render: (row) => (
                        <span className={`${MONO_CLASS} text-sm`}>
                            {`${row[field.name] ?? '—'}${field.suffix ?? ''}`}
                        </span>
                    ),
                })),
            {
                key: 'active',
                label: label('wms.common.status'),
                getValue: (row) => (row.active ? 1 : 0),
                render: (row) =>
                    row.active ? (
                        <Badge variant="success">{label('wms.common.active')}</Badge>
                    ) : (
                        <Badge variant="neutral">{label('wms.common.inactive')}</Badge>
                    ),
            },
            {
                key: 'actions',
                label: label('wms.common.actions'),
                sortable: false,
                searchable: false,
                exportable: false,
                getValue: () => '',
                render: (row) => (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                openEdit(row);
                            }}
                        >
                            {label('wms.common.edit')}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                setRowToToggle(row);
                            }}
                        >
                            {row.active ? (
                                <>
                                    <X size={14} className="mr-1" />
                                    {label('wms.common.deactivate')}
                                </>
                            ) : (
                                <>
                                    <Check size={14} className="mr-1" />
                                    {label('wms.common.activate')}
                                </>
                            )}
                        </Button>
                    </div>
                ),
            },
        ],
        [config, label, openEdit],
    );

    return (
        <section className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h2 className="text-lg font-semibold text-black dark:text-white">{config.title}</h2>
                    <p className="max-w-3xl text-sm text-mono-600 dark:text-mono-400">{config.description}</p>
                </div>
                <Button onClick={openCreate} className="w-full sm:w-auto">
                    <Plus size={18} className="mr-2" />
                    {`${label('wms.common.new')} — ${config.entityLabel}`}
                </Button>
            </div>

            {loading ? (
                <SkeletonTable columns={config.productFields.length + config.numberFields.length + 2} rows={5} />
            ) : loadError ? (
                <ErrorCard
                    title={config.title}
                    message={`${label('wms.common.load_error')}\n${loadError}`}
                    action={label('wms.common.retry')}
                    actionFn={loadRows}
                />
            ) : (
                <StandardTable
                    rows={rows}
                    columns={columns}
                    rowKey={(row, index) => row.id || index}
                    countLabel={config.countLabel}
                    exportFileName={config.exportFileName}
                    emptyMessage={label('wms.production.recipe_empty')}
                />
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={saving ? undefined : () => setModalOpen(false)}
                title={`${editingRow ? label('wms.common.edit') : label('wms.common.new')} — ${config.entityLabel}`}
                size="lg"
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Os produtos só entram na CRIAÇÃO: o backend não deixa
                        trocar o par de um registro existente (seria mudar o
                        significado de apontamentos já gravados contra ele). */}
                    {editingRow
                        ? config.productFields.map((field) => (
                              <div key={field.name}>
                                  <span className="mb-1 block text-sm font-medium text-mono-700 dark:text-mono-300">
                                      {field.fieldLabel}
                                  </span>
                                  <Sku value={editingRow[field.skuKey]} className="text-sm font-medium" />
                                  <span className="block text-sm text-mono-500 dark:text-mono-400">
                                      {editingRow[field.nameKey] || ''}
                                  </span>
                              </div>
                          ))
                        : config.productFields.map((field) => (
                              <ProductPicker
                                  key={field.name}
                                  label={label}
                                  fieldLabel={field.fieldLabel}
                                  value={products[field.name] ?? null}
                                  onChange={(row) => {
                                      setProducts((current) => ({ ...current, [field.name]: row }));
                                      setFormError(null);
                                  }}
                                  disabled={saving}
                              />
                          ))}

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {config.numberFields.map((field) => (
                            <div key={field.name}>
                                <Input
                                    label={field.fieldLabel}
                                    type="number"
                                    min={field.min}
                                    max={field.max}
                                    step={field.step ?? 'any'}
                                    value={numbers[field.name] ?? ''}
                                    onChange={(event) =>
                                        setNumbers((current) => ({ ...current, [field.name]: event.target.value }))
                                    }
                                    disabled={saving}
                                    className={MONO_CLASS}
                                    autoComplete="off"
                                    errorMessage={formError?.field === field.name ? formError.message : undefined}
                                />
                                {field.help ? (
                                    <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">{field.help}</p>
                                ) : null}
                            </div>
                        ))}
                    </div>

                    {formError && !formError.field ? (
                        <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                            {formError.message}
                        </p>
                    ) : null}

                    <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                        <Button type="submit" loading={saving} className="flex-1">
                            {label('wms.common.save')}
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setModalOpen(false)}
                            disabled={saving}
                            className="sm:w-auto"
                        >
                            {label('wms.common.cancel')}
                        </Button>
                    </div>
                </form>
            </Modal>

            <ConfirmModal
                isOpen={Boolean(rowToToggle)}
                onClose={() => setRowToToggle(null)}
                onConfirm={confirmToggle}
                loading={toggleLoading}
                title={rowToToggle?.active ? label('wms.common.deactivate') : label('wms.common.activate')}
                message={rowToToggle?.active ? label('wms.common.deactivate') : label('wms.common.activate')}
                confirmText={rowToToggle?.active ? label('wms.common.deactivate') : label('wms.common.activate')}
            />
        </section>
    );
};

export default ProductionRecipeManager;
